import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import ButtonWithLoading from "../components-atoms/ButtonWithLoadingIndicator";
import Checkbox from "@material-ui/core/Checkbox";
import Container from "@material-ui/core/Container";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import { Header } from "../components-molecules";
import Link from "@material-ui/core/Link";
import TextField from "@material-ui/core/TextField";
import { ThemeProvider } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { shopLogin } from "../redux/actions/shopLogin";
import { ITheme, colours, theme } from "../constants/theme";
import { login } from "../service/authService";

export default function ShopSignIn() {
  const classes = useStyles();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const signInLoading = useSelector((state: any) => state.login.loading);

  const dispatch = useDispatch();

  const signInButtonPressed = async () => {
    // Use the new secure login function from authService
    try {
      const response = await login(email, password, dispatch);
      
      if (response) {
        // If login is successful, dispatch the shopLogin action with the response
        const loginAction = shopLogin(response);
        loginAction(dispatch);
      }
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <Header />
        <Container component="main" maxWidth="xs">
          <Container className={classes.paper}>
            <ThemeProvider theme={theme}>
              <Typography
                component="h6"
                variant="h6"
                className={classes.signIntitle}
              >
                {t('login.signIn')}
              </Typography>
              <Typography component="p" variant="body2" color="textSecondary">
                {t('login.subtitle')}
              </Typography>
              <form className={classes.form} noValidate>
                <TextField
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label={t('login.emailAddress')}
                  name="email"
                  autoComplete="email"
                  autoFocus
                  InputProps={{
                    classes: {
                      root: classes.cssOutlinedInput,
                      focused: classes.cssFocused,
                      notchedOutline: classes.notchedOutline,
                    },
                  }}
                />
                <TextField
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label={t('common.password')}
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  InputProps={{
                    classes: {
                      root: classes.cssOutlinedInput,
                      focused: classes.cssFocused,
                      notchedOutline: classes.notchedOutline,
                    },
                  }}
                />
                <Grid
                  item
                  sm
                  container
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-start"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        value="remember"
                        color="primary"
                        onChange={(e: any) => setShowPassword(e.target.checked)}
                      />
                    }
                    label=""
                    style={{ marginRight: 0 }}
                  />
                  <Typography variant="body2" component="p">
                    {t('common.showPassword')}
                  </Typography>
                </Grid>
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item sm={8}>
                    <Link href="/forgotPassword" variant="body2">
                      <Typography variant="button" component="p">
                        {t('login.forgotPassword')}
                      </Typography>
                    </Link>
                  </Grid>
                  <Grid item container justifyContent="flex-end" sm={4}>
                    <ButtonWithLoading
                      onClick={signInButtonPressed}
                      loading={signInLoading}
                      title={t('login.signInButton')}
                    />
                  </Grid>
                </Grid>
              </form>
            </ThemeProvider>
          </Container>
        </Container>
      </ThemeProvider>
    </>
  );
}
const useStyles = makeStyles((theme: ITheme) => ({
  paper: {
    padding: " 24px",
    display: "flex",
    flexDirection: "column",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    boxSizing: "border-box",
    borderRadius: "20px",
    margin: "0",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "inherit",
  },
  signIntitle: {
    marginBottom: 12,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(3),
    alignItems: "center",
  },
  submit: {
    backgroundColor: colours.primary,
    borderRadius: "8px",
    padding: "6px 16px",
  },
  textField: {
    fontStyle: "normal",
    fontWeight: "normal",
    fontSize: "16px",
    lineHeight: "19px",
    letterSpacing: "0.25px",
    color: "rgba(0, 0, 0, 0.87)",
  },
  cssOutlinedInput: {
    "&$cssFocused $notchedOutline": {
      border: `1px solid ${theme.palette.primary.main}`,

    },
  },

  cssFocused: {},

  notchedOutline: {
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
}));
